import { parseBigIntToken } from "@/lib/tool-kit/math/bigint";
import { parseRational, type Rational } from "@/lib/tool-kit/math/rational";

/** Field readers shared by the sequence and summation calculators. */

const SEPARATORS = /[\s_  ]+/g;
const DIGIT_RUN = /\d+/g;
export const MAX_FIELD_DIGITS = 30;

export type RationalRead =
  | { kind: "ok"; value: Rational }
  | { kind: "empty" | "invalid" | "too-long" };

/** A number, decimal or fraction (`3`, `2.5`, `-7/2`), kept exact. */
export function readRationalField(raw: string): RationalRead {
  const text = raw.trim();
  if (text === "") {
    return { kind: "empty" };
  }
  const longest = Math.max(
    0,
    ...(text.match(DIGIT_RUN) ?? []).map((run) => run.length)
  );
  if (longest > MAX_FIELD_DIGITS) {
    return { kind: "too-long" };
  }
  const value = parseRational(text);
  return value === null ? { kind: "invalid" } : { kind: "ok", value };
}

export type CountRead =
  | { kind: "ok"; value: bigint }
  | { kind: "empty" | "invalid" | "too-small" | "too-large" };

/** A whole number of at least `min` and at most `max` (a term position or a count). */
export function readCountField(
  raw: string,
  min: bigint,
  max: bigint
): CountRead {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  const value = parseBigIntToken(text);
  if (value === null) {
    return { kind: "invalid" };
  }
  if (value < min) {
    return { kind: "too-small" };
  }
  return value > max ? { kind: "too-large" } : { kind: "ok", value };
}
